import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    SettingState,
    ValidationMessage,
    ValidationMessageType,
} from '@appblocks/codegeneration/types';

import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import TableItem from '@appblocks/codegeneration/SettingsConfigurations/TableItem';
import TableField from '@appblocks/codegeneration/SettingsConfigurations/TableField';
import FeatureUtils from '@appblocks/codegeneration/FeatureUtils';

const OWTABLENAME = 'OWSENS';

export default class UnoWireSensors extends BaseFeature {
    constructor() {
        super();
        this.name = '1-Wire/Single-Wire Sensors';
        this.id = 'unowiresensors';
        this.icon = 'BsThermometerSnow';
        this.requirements = ['TPP'];

        this.files = [
            'unowires.html',
        ];

        const settings = [
            {
                id: 'DEBUG_PRINT',
                name: 'Debug',
                settingType: SettingType.OPTION,
                default: '0',
                options: [
                    {
                        name: 'Disabled',
                        value: '0',
                    },
                    {
                        name: 'Enabled',
                        value: '1',
                    },
                ],
            },
            {
                id: 'SENSORS',
                name: 'Sensors',
                settingType: SettingType.ARRAY,
                required: true,
                refId: 'unowiresensor',
                default: new Array<BaseFeature>(0),
            },
        ];

        this.settings = this.settings.concat(settings);
    }

    getDependencies() {
        if (!this.getEnabled()) {
            return [];
        }
        const SENSORS = this.getSettingValue('SENSORS');
        for (let i = 0; i < SENSORS.length; i++) {
            if (SENSORS[i].getSettingValue('SENSORTYPE') === 'DS18B20') {
                return [
                    'tbl',
                    'web_dashboard',
                ];
            }
        }

        return [];
    }

    validate(): Array<ValidationMessage> {
        const messages: Array<ValidationMessage> = [];
        if (!this.getEnabled()) {
            return messages;
        }
        const project: ProjectConfiguration = this.project;
        const tables = project.getFeature('tbl')?.getSettingValue('TABLES');
        const SENSORS = this.getSettingValue('SENSORS');
        const ID = this.getID();
        let oneWireTable;
        const sockets: any = {};
        for (let i = 0; i < SENSORS.length; i += 1) {
            const tibbit = project.getFeature(SENSORS[i].getSettingValue('TIBBIT'));
            if (tibbit === undefined) {
                messages.push({
                    featureId: SENSORS[i].getID(),
                    settingId: 'TIBBIT',
                    messageType: ValidationMessageType.ERROR,
                    message: 'A tibbit must be selected.',
                });
                continue;
            }
            const tibbitName = tibbit.getSettingValue('NAME');
            const sensorType = SENSORS[i].getSettingValue('SENSORTYPE');
            const channel = Number(SENSORS[i].getSettingValue('CHANNEL')) - 1;
            if (sockets[tibbitName] === undefined) {
                sockets[tibbitName] = [
                    {
                        oneWire: 0,
                        singleWire: 0,
                    },
                    {
                        oneWire: 0,
                        singleWire: 0,
                    },
                ];
            }
            if (sensorType === 'DHT11' || sensorType === 'DHT22') {
                sockets[tibbitName][channel].singleWire += 1;
                if (sockets[tibbitName][channel].singleWire > 1) {
                    messages.push({
                        featureId: SENSORS[i].getID(),
                        settingId: 'CHANNEL',
                        messageType: ValidationMessageType.ERROR,
                        message: `Tibbit ${tibbitName} has more than one ${sensorType} sensor on channel ${channel + 1}`,
                    });
                }
            } else if (sensorType === 'DS18B20') {
                sockets[tibbitName][channel].oneWire += 1;
                if (sockets[tibbitName][channel].oneWire > 16) {
                    messages.push({
                        featureId: SENSORS[i].getID(),
                        settingId: 'SENSORTYPE',
                        messageType: ValidationMessageType.ERROR,
                        message: `${tibbitName} channel ${channel + 1} has more than 16 sensors`,
                    });
                }
            }
            if (sockets[tibbitName][channel].oneWire > 0
                && sockets[tibbitName][channel].singleWire > 0) {
                messages.push({
                    featureId: SENSORS[i].getID(),
                    settingId: 'SENSORTYPE',
                    messageType: ValidationMessageType.ERROR,
                    message: 'Each channel can either have 1 Single-Wire sensor or up to 16 One-Wire sensors',
                });
            }
        }
        for (let i = 0; i < tables.length; i += 1) {
            if (tables[i].getSettingValue('NAME') === OWTABLENAME) {
                oneWireTable = tables[i];
                break;
            }
        }
        if (oneWireTable === undefined) {
            oneWireTable = new TableItem();
            oneWireTable.initSettings(project);
            oneWireTable.setSettingValue('NAME', OWTABLENAME);
            oneWireTable.setSettingValue('KEYFIELDS', '1');

            const fieldID = new TableField();
            fieldID.initSettings(project);
            fieldID.setSettingValue('NAME', 'ID');
            fieldID.setSettingValue('TYPE', 'STRING');
            fieldID.setSettingValue('P1', '0');
            fieldID.setSettingValue('P2', '20');

            const fieldSerial = new TableField();
            fieldSerial.initSettings(project);
            fieldSerial.setSettingValue('NAME', 'SERIAL');
            fieldSerial.setSettingValue('TYPE', 'STRING');
            fieldSerial.setSettingValue('P1', '0');
            fieldSerial.setSettingValue('P2', '16');

            oneWireTable.setSettingValue('FIELDS', [
                fieldID,
                fieldSerial,
            ]);

            messages.push({
                messageType: ValidationMessageType.ADD,
                message: '',
                featureId: project.getFeature('tbl')?.getID(),
                settingId: 'TABLES',
                data: FeatureUtils.toJson(oneWireTable),
            });
        }

        return messages;
    }

    getDefines(): Array<Define> {
        const DEBUG_PRINT = this.getSettingValue('DEBUG_PRINT') === '1';
        if (DEBUG_PRINT) {
            return [
                {
                    name: 'TBT62_DEBUG_PRINT',
                    value: '1',
                },
                {
                    name: 'TBT62_BUSY_WAIT_MAX_TIME',
                    value: '2000',
                },
            ];
        }
        return [];
    }

    getEnabled(): boolean {
        const SENSORS = this.getSettingValue('SENSORS');
        return SENSORS.length > 0;
    }

    getCodeBlocks() {
        const project = this.project;
        let blocks: Array<CodeBlock> = [];

        if (project.runtime === 'simulator') {
            return blocks;
        }

        if (!this.getEnabled()) {
            return blocks;
        }

        blocks.push({
            location: 'DASHBOARD_SETUP_STATE',
            blockType: BlockType.LOCATION,
            template: `
        "unowiresEnabled": true,`,
        });

        const SENSORS = this.getSettingValue('SENSORS');
        const sensorItems = [];
        let scanCode = '';
        const tibbits = {} as any;
        let sensorCount = 0;
        let sensorSetCode = '';
        const bootCode = '';
        for (let i = 0; i < SENSORS.length; i += 1) {
            const tibbit = project.getFeature(SENSORS[i].getSettingValue('TIBBIT'));
            if (!tibbit) continue;
            const tibbitName = tibbit.getSettingValue('NAME');
            const sensorType = SENSORS[i].getSettingValue('SENSORTYPE');
            blocks = blocks.concat(SENSORS[i].getCodeBlocks());
            if (sensorType !== 'DS18B20') {
                continue;
            }
            sensorItems.push({
                id: SENSORS[i].getID(),
                name: SENSORS[i].getSettingValue('NAME'),
                channel: `${tibbitName}-CH${SENSORS[i].getSettingValue('CHANNEL')}`,
                serial: '',
            });
            const channel = SENSORS[i].getSettingValue('CHANNEL');
            const identifier = `${tibbitName}-CH${channel}`;
            if (tibbits[identifier] === undefined) {
                scanCode += `
    tbt62_scan_sensors(${tibbitName}_CHANNEL_NUM, ${Number(channel) - 1})`;
                tibbits[identifier] = identifier;
            }
            const VALUES = SENSORS[i].getSettingValue('VALUES');
            sensorSetCode += `
            case "${SENSORS[i].getID()}":
                var_${VALUES[0].getSettingValue('NAME')}_update_completed(value)`;
            sensorCount++;
        }

        blocks.push({
            location: 'SETUP_UNOWIRE_SENSORS',
            blockType: BlockType.CODE,
            template: `
        "unowire_sensors": ${JSON.stringify(sensorItems)},`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim tbt62_last_scan as dword = 0
dim tbt62_sensors as tbt62_sensor_info(unowire_sensor_count)`,
        });

        blocks.push({
            location: 'boot',
            blockType: BlockType.CODE,
            template: bootCode,
        });

        blocks.push({
            location: 'on_sys_timer',
            blockType: BlockType.CODE,
            template: `
if sys_timercountms - tbt62_last_scan > 2000 then
    dim tmpTemp as float=0.0
    dim ctr as byte=0
    dim SID as string(8)
    if unowire_sensor_count > 0 then
        for ctr=0 to unowire_sensor_count-1
            if tbt62_sensors(ctr).serial <> "" then 
                if tbt62_last_scan<>0 then
                    tbt62_ow_get_temp_separate(tmpTemp, tbt62_sensors(ctr).serial, TBT62_ONEWIRE_GET_TEMP_READ_DATA, tbt62_sensors(ctr).channel, tbt62_sensors(ctr).i2cchannel)
                    unowire_set_value(tbt62_sensors(ctr).id, tmpTemp)
                end if
                tbt62_ow_get_temp_separate(tmpTemp, tbt62_sensors(ctr).serial, TBT62_ONEWIRE_GET_TEMP_SAMPLE_START, tbt62_sensors(ctr).channel, tbt62_sensors(ctr).i2cchannel)
            end if
        next ctr
    end if
    dim sw_temp_tmp as float=0.0
    dim sw_hum_tmp as float=0.0
    '****single_wire_update****'
    tbt62_last_scan = sys.timercountms
end if`,
        });

        blocks.push({
            location: 'GLOBAL_CONSTS',
            blockType: BlockType.CODE,
            template: `
const unowire_sensor_count = ${sensorCount}`,
        });

        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare sub tbt62_scan_all()
declare sub tbt62_scan_sensors(i2c_channel as byte, tbt62channel as EN_TBT62_CHANNEL)
declare sub tbt62_save_serial(byref id as string, byref serial as string)
declare function tbt62_get_sensor(byref id as string) as string
declare sub unowire_setup(index as byte, byref id as string, channel as byte, i2cchannel as byte)
declare sub unowire_set_value(byref id as string, byref value as float)
type tbt62_sensor_info
    id as string(32)
    serial as string(16)
    channel as byte
    i2cchannel as byte
end type
declare tbt62_last_scan as dword
declare tbt62_sensors as tbt62_sensor_info(unowire_sensor_count)`,
        });

        blocks.push({
            location: 'callback_http_server_endpoint_called',
            blockType: BlockType.CODE,
            template: `
if server.request_file_name="unowires.html" then
    dim params as string=server.request_path
    if server.request_method<>"GET" then
        params=server.request_value_buffer
    end if

    dim f as string=http_server_find_param(params, "f") 'function
    callback_http_server_endpoint_called=true
    http_server_response_status(200)
    http_server_response_header("Content-Type", "text/plain")
    http_server_response_header_end()

    select case f
        case "get":
            dim id as string=http_server_find_param(params, "id")
            dim serial as string=tbt62_get_sensor(id)
            if serial<>"" then
                serial=tbt62_ID_Hex(serial)
            end if
            sock.setdata(serial)
            sock.send()
        case "scan":
            tbt62_scan_all()
        case "save":
            dim id as string=http_server_find_param(params, "id")
            dim serial as string=http_server_find_param(params, "serial")
            tbt62_save_serial(id, serial)
        case else:
    end select
end if

            `,
        });

        blocks.push({
            location: 'DEVICE_TBS',
            blockType: BlockType.CODE,
            template: `
sub unowire_setup(index as byte, byref id as string, channel as byte, i2cchannel as byte)
    dim tbt62_record_index as word=1
    tbt62_sensors(index).id=id
    tbt62_sensors(index).channel=channel
    tbt62_sensors(index).serial=""
    tbt62_sensors(index).i2cchannel=i2cchannel
    dim tbt62_serial as string(16)
    dim SID as string(8)
    tbl_select("${OWTABLENAME}","${OWTABLENAME}")
    tbl_record_find(EN_TBL_RECORD_ACTIVE,id,"ID",tbt62_record_index,EN_TBL_SEARCH_DOWN,PL_FD_FIND_EQUAL)
    if tbt62_record_index<>0  then
        tbl_record_sg(tbt62_record_index,EN_TBL_GET)
        tbl_field_sg("SERIAL",tbt62_serial,EN_TBL_GET)
        if tbt62_serial<>"" then
            dim i as byte=0
            for i=0 to 7
                SID=SID+chr(val("&h"+mid(tbt62_serial,i*2+1,2)))
            next i
            tbt62_sensors(index).serial=SID
        end if
    else
        tbl_web_add_row("${OWTABLENAME}",id+",")
    end if
end sub

function tbt62_get_sensor(byref id as string) as string
    dim i as byte=0
    for i=0 to unowire_sensor_count-1
        if tbt62_sensors(i).id = id then
            tbt62_get_sensor=tbt62_sensors(i).serial
        end if
    next i
end function

sub unowire_set_value(byref id as string, byref value as float)
    dim i as byte=0
    for i=0 to unowire_sensor_count-1
        if tbt62_sensors(i).id = id then
            select case tbt62_sensors(i).id
            ${sensorSetCode}
            case else:
            end select
        end if
    next i
end sub

sub tbt62_save_serial(byref id as string, byref serial as string)
    dim index as word=1
    dim row as string=id+","+serial
    tbl_select("${OWTABLENAME}","${OWTABLENAME}")
    dim result as en_tbl_status_codes=tbl_record_find(EN_TBL_RECORD_ACTIVE,id,"ID",index,EN_TBL_SEARCH_DOWN,PL_FD_FIND_EQUAL)
    if result=EN_TBL_STATUS_OK  then
        tbl_web_edit_row("${OWTABLENAME}",index,row)
    else
        tbl_web_add_row("${OWTABLENAME}",row)
    end if
    for index=0 to unowire_sensor_count-1
        if tbt62_sensors(index).id = id then
            dim SID as string=""
            if serial<>"" then
                dim i as byte=0
                for i=0 to 7
                    SID=SID+chr(val("&h"+mid(serial,i*2+1,2)))
                next i
                tbt62_sensors(index).serial=SID
            end if
        end if
    next index
end sub

sub tbt62_scan_all()
    ${scanCode}
end sub

sub tbt62_scan_sensors(i2c_channel as byte, tbt62channel as EN_TBT62_CHANNEL)
    'current socket is http request
    dim Sensor_ID as string
    dim temp as float
    sys.debugprint("Select channel - " + str(tbt62channel + 1))
    if tbt62_send_command(TBT62_CMD_SELECTCHANNEL + tbt62channel, i2c_channel)=false then
        sys.debugprint("send command fault!\\r\\n")
        exit sub
    else
        sys.debugprint("success\\r\\n")
    end if

    sys.debugprint("Set mode OneWire for channel " + str(tbt62channel + 1))
    ' Search devices and request temperature from found temperature sensors
    'Find first device
    sys.debugprint("Find first device - ")
    if tbt62_send_command(TBT62_CMD_SEARCHFIRST, i2c_channel)=false then
        sys.debugprint(" send command fault!\\r\\n")
        exit sub
    end if
    while (true)
        ' Wait BUSY
        sys.debugprint("finding. Wait BUSY - ")
        while (tbt62_checkBusy(i2c_channel) = false)
            doevents
        wend

        tbt62_send_command(TBT62_CMD_SELECTCHANNEL + tbt62channel, i2c_channel)

        ' Read results of search sensor
        sys.debugprint("Read ID - ")
        if not tbt62_read_data(Sensor_ID, 8, i2c_channel) then
            sys.debugprint("send command fault!\\r\\n")
            exit while
        end if

        ' Check Sensor ID
        if (Sensor_ID = (chr(255) + chr(255) + chr(255) + chr(255) + chr(255) + chr(255) + chr(255) + chr(255))) then
            sys.debugprint(" Sensors not found!\\r\\n")
            exit while
        end if

        sys.debugprint("Sensor ID: " + tbt62_ID_Hex(Sensor_ID))

        ' Get temperature
        sys.debugprint("Get temperature...\\r\\n")
        select case tbt62_ow_get_temperature(temp, Sensor_ID, tbt62channel, i2c_channel)
        case TBT62_NO_FAULT:
            #ifndef USE_IMPERIAL_UNITS
            sys.debugprint("Temp: " + ftostr(temp, FTOSTR_MODE_PLAIN, 5)+ "C\\r\\n")
            #else
            sys.debugprint("Temp: " + ftostr(temp, FTOSTR_MODE_PLAIN, 5)+ "F\\r\\n")
            #endif

        case TBT62_FAULT_TIBBIT:
            sys.debugprint(" send command fault!\\r\\n")
            exit sub

        case TBT62_FAULT_CONTROLSUM:
            sys.debugprint("Control sum fault!\\r\\n")

        case TBT62_FAULT_NO_SENSOR
            sys.debugprint("No sensor!\\r\\n")

        end select
        sock.setdata(tbt62_ID_Hex(Sensor_ID) + "\\t" + ftostr(temp, FTOSTR_MODE_PLAIN, 5) + "\\n")
        sock.send()
        while sock.txlen <> 0 AND sock.state <> PL_SST_CLOSED
            doevents
        wend

        tbt62_send_command(TBT62_CMD_SELECTCHANNEL + tbt62channel, i2c_channel)

        'Find next device
        sys.debugprint("Find next device - ")
        if not tbt62_send_command(TBT62_CMD_SEARCHNEXT, i2c_channel) then
            sys.debugprint(" send command fault!\\r\\n")
            exit sub
        end if
    wend
end sub`,
        });

        return blocks;
    }
}
